/**
 * NuAlpha Seasonal Campaign System — Client Engine
 * Time-Zone Authoritative: America/New_York (Eastern Time)
 * Supports: Halloween 2026, November 2026, Automatic Boundary Transitions & Fallbacks
 */
(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.nuSeasonalEngineInitialized) return;
  window.nuSeasonalEngineInitialized = true;

  const cfg = window.NuSeasonalCampaign || {};
  const campaigns = cfg.campaigns || {};
  let timerInterval = null;
  let activeCampaign = null;
  let simulatedTimeOffset = 0;

  // 1. Clock Synchronization & Simulation Harness
  function calculateCurrentTime() {
    const urlParams = new URLSearchParams(window.location.search);
    const simTimeStr = urlParams.get('campaign_sim_time');
    
    if (simTimeStr) {
      const parsed = Date.parse(simTimeStr);
      if (!isNaN(parsed)) {
        // If simulation is provided, run from that fixed or relative point
        if (!simulatedTimeOffset) {
          simulatedTimeOffset = parsed - Date.now();
          console.info('[NuAlpha Seasonal] Simulating time:', new Date(parsed).toISOString());
        }
        return Date.now() + simulatedTimeOffset;
      }
    }

    // Server-synchronized time
    if (cfg.serverTimeMs && cfg.clientLoadMs) {
      const serverOffset = cfg.serverTimeMs - cfg.clientLoadMs;
      return Date.now() + serverOffset;
    }

    return Date.now();
  }

  // 2. Resolve Active Campaign
  function evaluateCampaignStatus() {
    return { campaign: null, currentSec: 0, currentMs: 0 };
  }

  // 3. UI Updates: Announcement Bar
  function updateAnnouncementBar(camp, remainingSec) {
    const announceWrapper = document.getElementById('nuSeasonalAnnounceBar');
    const defaultAnnounce = document.getElementById('nuDefaultAnnounceContent');
    if (!announceWrapper) return;

    if (!camp) {
      // Return to default
      announceWrapper.classList.add('nu-announce--default');
      announceWrapper.classList.remove('nu-announce--halloween', 'nu-announce--november');
      if (defaultAnnounce) defaultAnnounce.style.display = 'flex';
      const campContent = document.getElementById('nuSeasonalAnnounceContent');
      if (campContent) campContent.style.display = 'none';
      return;
    }

    if (defaultAnnounce) defaultAnnounce.style.display = 'none';
    const campContent = document.getElementById('nuSeasonalAnnounceContent');
    if (campContent) campContent.style.display = 'flex';

    announceWrapper.classList.remove('nu-announce--default');
    announceWrapper.classList.toggle('nu-announce--halloween', camp.theme === 'halloween');
    announceWrapper.classList.toggle('nu-announce--november', camp.theme === 'november');

    // Update Text
    const textEl = document.getElementById('nuSeasonalAnnounceText');
    if (textEl) {
      textEl.innerText = camp.announcementText;
    }

    // Update Coupon
    const couponWrap = document.getElementById('nuSeasonalAnnounceCoupon');
    const couponCodeEl = document.getElementById('nuSeasonalAnnounceCode');
    if (couponWrap && couponCodeEl) {
      if (camp.couponCode) {
        couponCodeEl.innerText = camp.couponCode;
        couponWrap.style.display = 'inline-flex';
      } else {
        couponWrap.style.display = 'none';
      }
    }

    // Update Countdown Pill
    const cdWrap = document.getElementById('nuSeasonalAnnounceCd');
    if (cdWrap) {
      if (camp.hasCountdown && remainingSec > 0) {
        cdWrap.style.display = 'inline-flex';
        renderCountdown(remainingSec);
      } else {
        cdWrap.style.display = 'none';
      }
    }
  }

  // 4. Render Countdown Display
  function renderCountdown(totalSec) {
    if (totalSec < 0) totalSec = 0;

    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;

    const pad = (n) => String(n).padStart(2, '0');

    const cdText = days > 0
      ? `${days}d ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`
      : `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;

    const elTime = document.getElementById('nuCdTimeValue');
    if (elTime) elTime.innerText = cdText;

    // Update Hero Flip Boxes
    const daysEl = document.getElementById('nuHeroCdDays');
    const hoursEl = document.getElementById('nuHeroCdHours');
    const minsEl = document.getElementById('nuHeroCdMinutes');
    const secsEl = document.getElementById('nuHeroCdSeconds');
    if (daysEl) daysEl.innerText = pad(days);
    if (hoursEl) hoursEl.innerText = pad(hours);
    if (minsEl) minsEl.innerText = pad(minutes);
    if (secsEl) secsEl.innerText = pad(seconds);

    // Accessible description (updated once a minute or on final minute to avoid screen-reader noise)
    const srEl = document.getElementById('nuCdScreenReader');
    if (srEl && seconds === 0) {
      srEl.innerText = `Halloween offer expires in ${days} days, ${hours} hours, and ${minutes} minutes Eastern Daylight Time.`;
    }
  }

  // 5. UI Updates: Product & Homepage Hero Callout
  function updateProductHero(camp) {
    const hpBanner = document.getElementById('nuHpHeroSeasonalBanner');
    if (hpBanner) {
      hpBanner.style.display = (camp && camp.id === 'halloween_2026') ? 'block' : 'none';
    }

    const hpBadge = document.getElementById('nuHpHeroSeasonalBadge');
    if (hpBadge) {
      if (camp && camp.heroBadge && (!hpBanner || camp.id !== 'halloween_2026')) {
        hpBadge.innerText = camp.heroBadge;
        hpBadge.style.display = 'inline-flex';
      } else {
        hpBadge.style.display = 'none';
      }
    }

    const promoCard = document.getElementById('nuSeasonalHeroCard');
    if (!promoCard) return;

    if (!camp) {
      promoCard.style.display = 'none';
      return;
    }

    promoCard.style.display = 'block';
    promoCard.className = `nu-hero-seasonal-card nu-hero-seasonal--${camp.theme}`;

    const badgeEl = document.getElementById('nuHeroCardBadge');
    if (badgeEl) badgeEl.innerText = camp.heroBadge;

    const couponRow = document.getElementById('nuHeroCardCouponRow');
    const couponCode = document.getElementById('nuHeroCardCode');
    const noteEl = document.getElementById('nuHeroCardNote');

    if (camp.couponCode) {
      if (couponRow) couponRow.style.display = 'flex';
      if (couponCode) couponCode.innerText = camp.couponCode;
      if (noteEl) noteEl.innerText = camp.offerDescription;
    } else {
      if (couponRow) couponRow.style.display = 'none';
      if (noteEl) noteEl.innerText = camp.offerDescription;
    }

    const discEl = document.getElementById('nuHeroCardDisclaimer');
    if (discEl) {
      discEl.innerText = camp.disclaimer || '';
      discEl.style.display = camp.disclaimer ? 'block' : 'none';
    }

    syncVariantEligibility(camp);
  }

  // 6. Sync Variant Eligibility with Buy Button & Discount Permalink
  function syncVariantEligibility(camp) {
    const buyBtn = document.getElementById('pdHeroBuyBtn');
    if (!buyBtn) return;

    const selectedVariant = window.nuActiveVariant || window.nuMainVariantId;
    const selectedQty = window.nuActiveQuantity || 1;
    const isEligible = camp && camp.eligibleVariants && camp.eligibleVariants.includes(String(selectedVariant));

    // Update Eligibility Notice
    const eligNotice = document.getElementById('nuHeroEligibilityNotice');
    if (eligNotice) {
      if (camp && camp.couponCode) {
        eligNotice.style.display = 'block';
        if (isEligible) {
          eligNotice.innerHTML = `✅ <strong>$5 Coupon Pre-Applied:</strong> Code <code>${camp.couponCode}</code> automatically saves $5 at checkout on this multi-pack!`;
          eligNotice.className = 'nu-eligibility-notice nu-eligibility--success';
        } else {
          eligNotice.innerHTML = `ℹ️ <strong>Standard Single Pack:</strong> Coupon <code>${camp.couponCode}</code> is valid for 2-Pack, 3-Pack, or 6-Pack options.`;
          eligNotice.className = 'nu-eligibility-notice nu-eligibility--info';
        }
      } else {
        eligNotice.style.display = 'none';
      }
    }

    // Update Direct Checkout URL with ?discount=HALLOWEEN5
    const baseHref = '/cart/' + selectedVariant + ':' + selectedQty;
    const url = new URL(baseHref, window.location.origin);
    
    // Preserve existing search params (e.g. tracking, click_id)
    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((val, key) => {
      if (key !== 'discount' && key !== 'campaign_sim_time') {
        url.searchParams.set(key, val);
      }
    });

    if (isEligible && camp.couponCode) {
      url.searchParams.set('discount', camp.couponCode);
    } else {
      url.searchParams.delete('discount');
    }

    buyBtn.href = url.pathname + url.search;
  }

  // 7. Timer Tick Cycle
  function tick() {
    const status = evaluateCampaignStatus();
    const camp = status.campaign;

    // Check if campaign transitioned
    if (!activeCampaign && camp) {
      activeCampaign = camp;
      document.documentElement.setAttribute('data-nu-campaign', camp.id);
      dispatchTracking('campaign_view', camp.id);
    } else if (activeCampaign && (!camp || activeCampaign.id !== camp.id)) {
      activeCampaign = camp;
      if (camp) {
        document.documentElement.setAttribute('data-nu-campaign', camp.id);
        dispatchTracking('campaign_view', camp.id);
      } else {
        document.documentElement.removeAttribute('data-nu-campaign');
      }
    }

    let remainingSec = 0;
    if (camp && camp.hasCountdown) {
      remainingSec = camp.endEpoch - status.currentSec;
    }

    updateAnnouncementBar(camp, remainingSec);
    updateProductHero(camp);
  }

  // 8. Custom Analytics Dispatcher
  function dispatchTracking(eventName, value) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, { campaign_id: value });
      }
      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', eventName, { campaign: value });
      }
    } catch (e) {}
  }

  // 9. Interactive Copy Code Function
  window.nuCopyCouponCode = function(code, btnEl) {
    if (!code) return;
    navigator.clipboard.writeText(code).then(() => {
      if (btnEl) {
        const orig = btnEl.innerText;
        btnEl.innerText = 'COPIED!';
        btnEl.classList.add('is-copied');
        setTimeout(() => {
          btnEl.innerText = orig;
          btnEl.classList.remove('is-copied');
        }, 2200);
      }
      dispatchTracking('coupon_copy', code);
    }).catch(() => {
      // Fallback prompt
      window.prompt('Copy coupon code:', code);
    });
  };

  // 10. Initialization & Listeners
  function init() {
    tick();

    // Clear existing timer if any
    if (timerInterval) clearInterval(timerInterval);
    timerInterval = setInterval(tick, 1000);

    // Variant change listener
    document.addEventListener('nu:variant-changed', () => {
      syncVariantEligibility(activeCampaign);
    });

    // Cleanup on Shopify theme section unload / reloads
    document.addEventListener('shopify:section:unload', () => {
      if (timerInterval) clearInterval(timerInterval);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
